import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoConflitoIgnorado,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
  CargaTrabalhoObrasData,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissionaisData,
  IsoDate,
} from "./carga-trabalho.types";

const MS_PER_DAY = 86_400_000;

const MONTH_LABELS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Converte `YYYY-MM-DD` em número de dias (UTC), sem deslocamento de fuso. */
export function toDayNumber(date: IsoDate): number {
  const [year = 1970, month = 1, day = 1] = date.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

export function fromDayNumber(day: number): IsoDate {
  const date = new Date(day * MS_PER_DAY);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Data local de hoje no formato do contrato. */
export function todayIso(now: Date = new Date()): IsoDate {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `YYYY-MM-DD` -> `DD/MM/YYYY`. */
export function formatDate(date: IsoDate): string {
  const [year, month, day] = date.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

/** `YYYY-MM-DD` -> `DD/MM`. */
export function formatDayMonth(date: IsoDate): string {
  const [, month, day] = date.slice(0, 10).split("-");
  return `${day}/${month}`;
}

/** Duração inclusiva em dias. */
export function durationInDays(intervalo: CargaTrabalhoIntervalo): number {
  return toDayNumber(intervalo.fim) - toDayNumber(intervalo.inicio) + 1;
}

/** Duração resumida para rótulos: meses quando >= 30 dias (ex.: "1m"), senão dias (ex.: "15d"). */
export function formatDurationShort(intervalo: CargaTrabalhoIntervalo): string {
  const days = durationInDays(intervalo);
  if (days >= 30) return `${Math.round(days / 30)}m`;
  return `${days}d`;
}

/** Período padrão: ano corrente, de janeiro a dezembro. */
export function getDefaultPeriodo(now: Date = new Date()): CargaTrabalhoPeriodo {
  const year = now.getFullYear();
  return { inicio: `${year}-01-01`, fim: `${year}-12-31` };
}

/** Valor `YYYY-MM` -> primeiro dia do mês. */
export function monthStart(value: string): IsoDate {
  return `${value}-01`;
}

/** Valor `YYYY-MM` -> último dia do mês. */
export function monthEnd(value: string): IsoDate {
  const [year = 1970, month = 1] = value.split("-").map(Number);
  return fromDayNumber(Math.floor(Date.UTC(year, month, 1) / MS_PER_DAY) - 1);
}

export function periodoToMonthValues(periodo: CargaTrabalhoPeriodo): { from: string; to: string } {
  return { from: periodo.inicio.slice(0, 7), to: periodo.fim.slice(0, 7) };
}

/** Máximo de meses por consulta, para manter a tela legível e a API leve. */
export const MAX_PERIODO_MONTHS = 36;

export const PERIODO_REQUIRED_MESSAGE = "Informe o período da consulta.";
export const PERIODO_INVALID_MESSAGE = "A data final não pode ser anterior à data inicial.";
export const PERIODO_TOO_LONG_MESSAGE = `O período não pode ultrapassar ${MAX_PERIODO_MONTHS} meses.`;

/** Validação do período em tela; devolve a mensagem de erro ou `null`. */
export function validatePeriodo(from: string, to: string): string | null {
  const pattern = /^\d{4}-\d{2}$/;
  if (!pattern.test(from) || !pattern.test(to)) return PERIODO_REQUIRED_MESSAGE;
  if (from > to) return PERIODO_INVALID_MESSAGE;
  const [fy = 0, fm = 0] = from.split("-").map(Number);
  const [ty = 0, tm = 0] = to.split("-").map(Number);
  if ((ty - fy) * 12 + (tm - fm) + 1 > MAX_PERIODO_MONTHS) return PERIODO_TOO_LONG_MESSAGE;
  return null;
}

export function formatPeriodoLabel(periodo: CargaTrabalhoPeriodo): string {
  const [fy = "", fm = ""] = periodo.inicio.split("-");
  const [ty = "", tm = ""] = periodo.fim.split("-");
  return `${fm}/${fy.slice(2)} – ${tm}/${ty.slice(2)}`;
}

export interface TimelineMonth {
  key: string;
  year: number;
  label: string;
  /** Posição e largura em % da linha do tempo. */
  left: number;
  width: number;
}

export interface TimelineYear {
  year: number;
  left: number;
  width: number;
}

export interface Timeline {
  startDay: number;
  endDay: number;
  totalDays: number;
  months: TimelineMonth[];
  years: TimelineYear[];
  /** Dia (UTC) em que a rolagem horizontal começa: o início do período ou, onde a tela pede, a 1ª alocação. */
  focusDay?: number;
}

/** Monta a escala temporal: meses proporcionais ao número de dias. */
export function buildTimeline(
  periodo: CargaTrabalhoPeriodo,
  focusInicio?: CargaTrabalhoPeriodo["inicio"],
): Timeline {
  const startDay = toDayNumber(periodo.inicio);
  const endDay = toDayNumber(periodo.fim);
  const totalDays = Math.max(endDay - startDay + 1, 1);

  const months: TimelineMonth[] = [];
  let cursor = startDay;
  while (cursor <= endDay) {
    const date = new Date(cursor * MS_PER_DAY);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const nextMonthStart = Math.floor(Date.UTC(year, month + 1, 1) / MS_PER_DAY);
    const last = Math.min(nextMonthStart - 1, endDay);
    months.push({
      key: `${year}-${pad(month + 1)}`,
      year,
      label: MONTH_LABELS[month] ?? "",
      left: ((cursor - startDay) / totalDays) * 100,
      width: ((last - cursor + 1) / totalDays) * 100,
    });
    cursor = last + 1;
  }

  const years: TimelineYear[] = [];
  for (const month of months) {
    const current = years[years.length - 1];
    if (current && current.year === month.year) current.width += month.width;
    else years.push({ year: month.year, left: month.left, width: month.width });
  }

  const focusDay = focusInicio ? toDayNumber(focusInicio) : undefined;
  return { startDay, endDay, totalDays, months, years, focusDay };
}

export interface BarPosition {
  left: number;
  width: number;
  clippedStart: boolean;
  clippedEnd: boolean;
}

/** Posição (em %) de um intervalo na escala; `null` quando fora do período. */
export function positionInterval(
  intervalo: CargaTrabalhoIntervalo,
  timeline: Timeline,
): BarPosition | null {
  const start = toDayNumber(intervalo.inicio);
  const end = toDayNumber(intervalo.fim);
  if (end < timeline.startDay || start > timeline.endDay || end < start) return null;

  const clippedStart = start < timeline.startDay;
  const clippedEnd = end > timeline.endDay;
  const from = Math.max(start, timeline.startDay);
  const to = Math.min(end, timeline.endDay);

  return {
    left: ((from - timeline.startDay) / timeline.totalDays) * 100,
    width: ((to - from + 1) / timeline.totalDays) * 100,
    clippedStart,
    clippedEnd,
  };
}

/** Posição (em %) do dia atual; `null` quando fora do período (RN09). */
export function positionToday(timeline: Timeline, today: IsoDate): number | null {
  const day = toDayNumber(today);
  if (day < timeline.startDay || day > timeline.endDay) return null;
  return ((day - timeline.startDay + 0.5) / timeline.totalDays) * 100;
}

/** RN10 — alocação integralmente anterior ao dia atual (finalizada). */
export function isPast(intervalo: CargaTrabalhoIntervalo, today: IsoDate): boolean {
  return toDayNumber(intervalo.fim) < toDayNumber(today);
}

/** Obra cancelada, a partir da situação devolvida pela API. */
export function isObraCancelada(obra: Pick<CargaTrabalhoObra, "situacao">): boolean {
  return obra.situacao.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().startsWith("cancelad");
}

export function overlaps(a: CargaTrabalhoIntervalo, b: CargaTrabalhoIntervalo): boolean {
  return toDayNumber(a.inicio) <= toDayNumber(b.fim) && toDayNumber(b.inicio) <= toDayNumber(a.fim);
}

export interface ConflitoAlocacao {
  a: CargaTrabalhoAlocacao;
  b: CargaTrabalhoAlocacao;
  /** Trecho em que as duas alocações se sobrepõem. */
  inicio: IsoDate;
  fim: IsoDate;
}

/**
 * RN08 — conflito é a sobreposição entre alocações de um mesmo profissional.
 * Alocações de obras canceladas não geram conflito. Retorna um conflito por par.
 */
export function calcularConflitos(alocacoes: CargaTrabalhoAlocacao[]): ConflitoAlocacao[] {
  const ativas = alocacoes
    .filter((alocacao) => !isObraCancelada(alocacao.obra))
    .sort((x, y) => toDayNumber(x.inicio) - toDayNumber(y.inicio));

  const conflitos: ConflitoAlocacao[] = [];
  for (const [i, first] of ativas.entries()) {
    for (const second of ativas.slice(i + 1)) {
      if (toDayNumber(second.inicio) > toDayNumber(first.fim)) break;
      if (!overlaps(first, second)) continue;
      const inicio = toDayNumber(second.inicio);
      const fim = Math.min(toDayNumber(first.fim), toDayNumber(second.fim));
      conflitos.push({
        a: first,
        b: second,
        inicio: fromDayNumber(inicio),
        fim: fromDayNumber(fim),
      });
    }
  }
  return conflitos;
}

/** Chave estável de um conflito: as alocações envolvidas, em ordem. */
export function conflitoKey(alocacaoIds: string[]): string {
  return [...alocacaoIds].sort().join("|");
}

/**
 * RN07 (HU 2434) — separa os conflitos em abertos e ignorados. Um conflito é
 * ignorado quando todas as suas alocações pertencem a um mesmo registro de
 * conflito ignorado.
 */
export function separarConflitos(
  conflitos: ConflitoAlocacao[],
  ignorados: CargaTrabalhoConflitoIgnorado[] = [],
): { abertos: ConflitoAlocacao[]; ignorados: ConflitoAlocacao[] } {
  const abertos: ConflitoAlocacao[] = [];
  const tratados: ConflitoAlocacao[] = [];
  for (const conflito of conflitos) {
    const isIgnorado = ignorados.some(
      ({ alocacaoIds }) =>
        alocacaoIds.includes(conflito.a.id) && alocacaoIds.includes(conflito.b.id),
    );
    (isIgnorado ? tratados : abertos).push(conflito);
  }
  return { abertos, ignorados: tratados };
}

export function conflitoLabel(total: number): string {
  return total === 1 ? "1 Conflito" : `${total} Conflitos`;
}

/**
 * Distribui itens em faixas (linhas) para que intervalos sobrepostos não se
 * escondam. Devolve o índice da faixa de cada item, na ordem recebida.
 * `minDias` (um número, ou um por item) faz cada item reservar ao menos esse número de dias, mesmo que seu intervalo
 * seja menor: quem desenha a barra com largura mínima e rótulo externo (a indisponibilidade curta) não a sobrepõe à
 * seguinte, que cai numa faixa abaixo.
 */
export function assignLanes(
  intervalos: CargaTrabalhoIntervalo[],
  minDias: number | readonly number[] = 1,
): number[] {
  const order = intervalos
    .map((intervalo, index) => ({ intervalo, index }))
    .sort((x, y) => toDayNumber(x.intervalo.inicio) - toDayNumber(y.intervalo.inicio));
  const laneEnds: number[] = [];
  const lanes = new Array<number>(intervalos.length).fill(0);

  for (const { intervalo, index } of order) {
    const start = toDayNumber(intervalo.inicio);
    let lane = laneEnds.findIndex((end) => end < start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = Math.max(
      toDayNumber(intervalo.fim),
      start + (typeof minDias === "number" ? minDias : (minDias[index] ?? 1)) - 1,
    );
    lanes[index] = lane;
  }
  return lanes;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function collectDates(
  data: CargaTrabalhoProfissionaisData | CargaTrabalhoObrasData | null,
): IsoDate[] {
  if (!data) return [];

  const dates: IsoDate[] = [];
  const addIntervalo = (intervalo: CargaTrabalhoIntervalo | null | undefined) => {
    if (intervalo) dates.push(intervalo.inicio, intervalo.fim);
  };
  const addObra = (obra: CargaTrabalhoObra) => {
    addIntervalo(obra.etapas.planejamento);
    addIntervalo(obra.etapas.obra);
    addIntervalo(obra.etapas.fechamento);
  };

  if ("profissionais" in data) {
    for (const profissional of data.profissionais) {
      profissional.indisponibilidades.forEach(addIntervalo);
      for (const alocacao of profissional.alocacoes) {
        addIntervalo(alocacao);
        addObra(alocacao.obra);
      }
    }
  } else {
    for (const obra of data.obras) {
      addObra(obra);
      obra.profissionais.forEach(addIntervalo);
    }
  }

  return dates.filter((date) => ISO_DATE.test(date));
}

/**
 * Estende o período consultado (em meses inteiros) até cobrir as barras por completo,
 * para que obras que seguem além do período possam ser vistas rolando o Gantt na horizontal.
 * O período escolhido pelo usuário nunca encolhe.
 */
export function extendPeriodo(
  periodo: CargaTrabalhoPeriodo,
  data: CargaTrabalhoProfissionaisData | CargaTrabalhoObrasData | null,
): CargaTrabalhoPeriodo {
  const dates = collectDates(data).sort();
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return periodo;

  return {
    inicio: first < periodo.inicio ? monthStart(first.slice(0, 7)) : periodo.inicio,
    fim: last > periodo.fim ? monthEnd(last.slice(0, 7)) : periodo.fim,
  };
}

/**
 * Ajusta o período para anos inteiros (1º de janeiro a 31 de dezembro): o cabeçalho do
 * Gantt sempre mostra os 12 meses de cada ano que aparece, mesmo com meses sem alocações.
 */
export function snapToYears(periodo: CargaTrabalhoPeriodo): CargaTrabalhoPeriodo {
  return {
    inicio: `${periodo.inicio.slice(0, 4)}-01-01`,
    fim: `${periodo.fim.slice(0, 4)}-12-31`,
  };
}
